import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { t } from 'i18next';

import { savePartnerApiKey, deletePartnerApiKey, loadPartnerIntegration, activatePartnerIntegration, deactivatePartnerIntegration } from '../modules/appIntegrations';
import { UpgradePlanHint } from './PricingPlans';

// translation integrations: the backend refuses their data on the free plan, so they are locked
// before enabling instead of failing afterwards
export const PAID_PARTNER_SLUGS = ['bogos', 'size_chart_avada'];

const IOSSwitch = styled((props) => (
  <Switch disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  margin: theme.spacing(1),
  '& .MuiSwitch-switchBase': {
    padding: 1,
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: theme.palette.common.white,
      '& + .MuiSwitch-track': {
        backgroundColor: '#52d869',
        opacity: 1,
        border: 'none',
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#52d869',
      border: '6px solid #fff',
    },
  },
  '& .MuiSwitch-thumb': {
    width: 24,
    height: 24,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    border: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[50],
    opacity: .5,
    transition: theme.transitions.create(['background-color', 'border']),
  },
}));

const styles = {
  enableIntegrationBtn: {
    color: 'white',
    backgroundColor: 'green',
    borderColor: 'green',
    '&:hover': {
      backgroundColor: 'darkgreen',
      borderColor: 'darkgreen',
    }
  },
  disableIntegrationBtn: {
    color: 'white',
    backgroundColor: 'red',
    borderColor: 'red',
    '&:hover': {
      backgroundColor: 'darkred',
      borderColor: 'darkred',
    }
  },
  removeBtn: {
    marginRight: 'auto',
    color: 'white',
    backgroundColor: 'red',
    borderColor: 'red',
    '&:hover': {
      backgroundColor: 'darkred',
      borderColor: 'darkred',
    }
  },
  apiKeyInputContainer: {
    position: 'relative'
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '10px 16px',
    borderTop: '1px solid var(--ly-divider, rgba(0, 0, 0, 0.12))',
  },
  btnIcon: {
    marginRight: '8px',
  },
  // Weisser Lade-Schleier, im Dark Mode eine aufleuchtende Flaeche.
  // --ly-backdrop ist dieselbe Quelle, die auch das MuiBackdrop-Override nutzt.
  loadingLayer: {
    position: 'absolute',
    background: 'var(--ly-backdrop)',
    width: '100%',
    height: '100%',
    display: 'flex',
    left: 0,
    top: 0,
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  }
};

export default function PartnerIntegrationsSettings(props) {
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const [apiKey, setApiKey] = React.useState('');
  const [error, setError] = React.useState(null);

  const save_partner_apikey_pending = useSelector(state => state.appIntegrations.save_partner_apikey_pending);
  const delete_partner_apikey_pending = useSelector(state => state.appIntegrations.delete_partner_apikey_pending);
  const load_partner_integration_pending = useSelector(state => state.appIntegrations.load_partner_integration_pending);
  const activate_partner_integration_pending = useSelector(state => state.appIntegrations.activate_partner_integration_pending);
  const deactivate_partner_integration_pending = useSelector(state => state.appIntegrations.deactivate_partner_integration_pending);
  const loadingPartnerSlug = useSelector(state => state.appIntegrations.loadingPartnerSlug);
  const shopPlan = useSelector(state => state.shop.shop.charge?.name);
  const partnerIntegration = useSelector(state => state.appIntegrations.partnerIntegration);
  const partnerIntegrationError = useSelector(state => state.appIntegrations.partnerIntegrationError);

  const loading = save_partner_apikey_pending || delete_partner_apikey_pending || load_partner_integration_pending || activate_partner_integration_pending || deactivate_partner_integration_pending;
  const isThisPartnerLoading = loadingPartnerSlug === props.partnerHandle;
  const prevSavePending = React.useRef(save_partner_apikey_pending);
  const prevDeletePending = React.useRef(delete_partner_apikey_pending);

  // componentDidMount
  useEffect(() => {

  }, [apiKey])


  // Update apiKey when partnerIntegration is loaded
  useEffect(() => {
    if (partnerIntegration?.data?.api_key) {
      setApiKey(partnerIntegration.data.api_key);
    }
  }, [partnerIntegration]);

  // Handle save completion
  useEffect(() => {
    if (prevSavePending.current && !save_partner_apikey_pending) {
      if (partnerIntegrationError) {
        // Show error message
        setError(partnerIntegrationError);
      } else if (partnerIntegration?.data?.api_key) {
        // Success - close dialog after a delay
        setTimeout(() => {
          setOpen(false);
          setError(null);
        }, 1000);
      }
    }
    prevSavePending.current = save_partner_apikey_pending;
  }, [save_partner_apikey_pending, partnerIntegration, partnerIntegrationError]);

  // Close dialog when delete completes
  useEffect(() => {
    if (prevDeletePending.current && !delete_partner_apikey_pending) {
      setTimeout(() => {
        setOpen(false);
      }, 500);
    }
    prevDeletePending.current = delete_partner_apikey_pending;
  }, [delete_partner_apikey_pending]);


  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeApiKey = (event) => {
    setApiKey(event.target.value);
    setError(null);
  };

  const handleSaveApiKey = () => {
    setError(null);
    dispatch(savePartnerApiKey(props.partnerHandle, apiKey));
  };

  const handleDeleteApiKey = () => {
    setApiKey('');
    setError(null);
    dispatch(deletePartnerApiKey(props.partnerHandle));
  };

  const handleEnableIntegration = () => {
    if (props.activeIntegration) {
      // Deactivate
      dispatch(deactivatePartnerIntegration(props.partnerHandle));
    } else {
      setOpen(true);
      // Activate
      dispatch(activatePartnerIntegration(props.partnerHandle));
    }
  };

  const handleEnableApiIntegration = () => {
    if (props.activeIntegration) {
      // Deactivate
      dispatch(deactivatePartnerIntegration(props.partnerHandle));
    } else {
      setOpen(true);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
    dispatch(loadPartnerIntegration(props.partnerHandle));
  };


  const usesApiKey = props.partnerData.partnerIntegration.modules.modules?.includes('apiKey');
  // an integration enabled before a downgrade keeps its switch: turning Bogos off deletes its
  // translations, which a free shop would otherwise get back on upgrade
  const lockedOnPlan = shopPlan === 'free' && PAID_PARTNER_SLUGS.includes(props.partnerHandle) && !props.activeIntegration;

  // One labelled row of its own on the card, not a bare switch over the banner image: the label
  // says what the switch does, the settings button sits next to it once the app is enabled
  const toggleRow = (onChange) => lockedOnPlan ? (
    <div style={{ ...styles.toggleRow, position: 'relative' }}>
      <Typography variant="body2" component="span" color="text.disabled" style={{ fontWeight: 500 }}>
        {t('partnerIntegrations.enableToggleLabel')}
      </Typography>
      {/* invisible, it only keeps the row as tall as the unlocked ones */}
      <IOSSwitch disabled style={{ visibility: 'hidden' }} inputProps={{ 'aria-hidden': true, tabIndex: -1 }} />
      {/* to the right: below the row the tooltip would cover the card's "Get the app" button */}
      <UpgradePlanHint neededPlan={'basic'} position="end" placement="right" isOverlay />
    </div>
  ) : (
    <div style={styles.toggleRow}>
      <Typography variant="body2" component="span" style={{ fontWeight: 500 }}>
        {t(props.activeIntegration ? 'partnerIntegrations.enabledToggleLabel' : 'partnerIntegrations.enableToggleLabel')}
      </Typography>
      <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {usesApiKey && props.activeIntegration && (
          <Button variant="outlined" onClick={handleClickOpen} size="small">
            {t('partnerIntegrations.settingsBtn')}
          </Button>
        )}
        {/* the switch only flips once the backend confirms; until then it is disabled, and the
            spinner says why */}
        {isThisPartnerLoading && <CircularProgress size={18} />}
        <IOSSwitch
          checked={props.activeIntegration ? true : false}
          onChange={onChange}
          disabled={isThisPartnerLoading}
          inputProps={{ 'aria-label': t('partnerIntegrations.enableToggleLabel') }}
        />
      </span>
    </div>
  );

  return (
    <div>
      {usesApiKey ?
        <>
          {toggleRow(handleEnableApiIntegration)}
          <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">{t('partnerIntegrations.apiDialog.title')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {t('partnerIntegrations.apiDialog.description', {appName: props.partnerData.title})}
              </DialogContentText>
              <div style={styles.apiKeyInputContainer}>
                <TextField
                  autoFocus
                  margin="dense"
                  id="api_key"
                  label={t('partnerIntegrations.apiDialog.inputLabel')}
                  type="text"
                  placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                  value={apiKey}
                  onChange={handleChangeApiKey}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={error ? true : false}
                  helperText={error ? error : null} />      
              </div>
              {loading && 
                <div style={styles.loadingLayer}>
                  <p>
                    <CircularProgress />
                  </p>
                  <p>loading...</p>
                </div>
              }
            </DialogContent>
            <DialogActions>
              {apiKey &&
                <Button onClick={handleDeleteApiKey} disabled={apiKey === ''} color="primary" sx={styles.removeBtn} variant="contained">
                  <HighlightOffOutlinedIcon sx={styles.btnIcon} /> {t('partnerIntegrations.apiDialog.deleteBtn')}
                </Button>
              }
              <Button onClick={handleClose} variant="outlined">
                {t('system.cancel')}
              </Button>
              <Button onClick={handleSaveApiKey} disabled={false} color="primary" variant="contained">
                <DoneOutlinedIcon sx={styles.btnIcon} /> {t('partnerIntegrations.apiDialog.saveBtn')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      :
        toggleRow(handleEnableIntegration)
      }
    </div>
  );
}